"use client";

import { type JSX } from "react";
import { RichText, type LinkField } from "@sitecore-content-sdk/nextjs";

import { SectionHeaderProps } from "components/section-header/section-header.props";

import Button from "../../atoms/Button";
import { RichTextWithTable } from "../../molecules/RichTextWithTable/RichTextWithTables";
import {
  extractHiddenBlock,
  getConfiguredHeadingStyles,
} from "../../utils/styles";
import styles from "./SectionHeader.module.scss";

const DARK_BG_COLORS = ["bg-dark-blue", "bg-ecolab-blue"];

const computeIsDarkBg = (rawStyles: string | undefined): boolean => {
  const stylesArray = rawStyles ? rawStyles.split(" ") : [];
  if (stylesArray.length === 0) return false;
  const bgColor = DARK_BG_COLORS.find((cls) => stylesArray.includes(cls));
  return Boolean(
    (bgColor && bgColor !== "") || stylesArray.includes("text-white"),
  );
};

const hasCtaLink = (cta?: LinkField): boolean =>
  Boolean(cta?.value?.href?.trim());

const SectionHeaderDefaultComponent = (
  props: SectionHeaderProps,
): JSX.Element => (
  <div
    className={`component sectionHeader ${props?.params?.styles ?? ""} ${
      props?.fields ? "" : "emptyFields"
    }`}
  >
    <div className="component-content">
      <span className="is-empty-hint">SectionHeader</span>
    </div>
  </div>
);

export const SectionHeaderDefault = (
  props: SectionHeaderProps,
): JSX.Element => {
  console.log("---->[SectionHeaderDefault] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;

  if (!fields) return <SectionHeaderDefaultComponent {...props} />;

  const id = params?.RenderingIdentifier;
  const dividerStyle =
    params?.NoDivider === "1" ? "" : styles.sectionHeaderDefaultTitle;
  const isDarkBg = computeIsDarkBg(params?.Styles);
  const textColor = isDarkBg ? "text-white" : "text-text-gray";

  const typeOfTitleTag = params?.TypeOfTitleTag?.toLowerCase();
  const typeOfSubtitleTag = params?.TypeOfSubtitleTag?.toLowerCase() || "h3";
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);
  const hasAnyCTA =
    hasCtaLink(fields.PrimaryCTA) || hasCtaLink(fields.SecondaryCTA);
  const paddingClass = hasAnyCTA
    ? "pb-[var(--global-component-spacing)]"
    : "pb-0";
  const xmcStyles = props?.params?.Styles?.replace("text-8xl", "");
  const variantSuffix = isDarkBg ? "-reverse" : "";

  const primaryCtaClass = fields.PrimaryCTA?.value?.class;
  const secondaryCtaClass = fields.SecondaryCTA?.value?.class;

  const primaryButtonType = primaryCtaClass || `primary${variantSuffix}`;
  const secondaryButtonType = secondaryCtaClass || `secondary${variantSuffix}`;

  const primaryIconName = primaryCtaClass ? "CHEVRON_BLUE_RIGHT" : "";
  const secondaryIconName = secondaryCtaClass ? "CHEVRON_BLUE_RIGHT" : "";

  return (
    <div
      id={id ?? undefined}
      className={`section-header-default-component px-[var(--spacing-sixteen)] ${paddingClass} pt-4 ${styles.sectionHeaderDefault} ${xmcStyles} ${hiddenBlockStyles}`}
      data-testid="section-header-default-component"
      data-role="section-header-default-component"
    >
      {(isPageEditing || fields.Title?.value) && (
        <RichText
          tag={typeOfTitleTag || "h2"}
          field={fields.Title}
          className={`${dividerStyle} richtext-content relative ${textColor} ${
            typeOfTitleTag
              ? getConfiguredHeadingStyles(typeOfTitleTag)
              : "text-[34px] leading-[42.16px] lg:text-[42px] lg:leading-[52.08px]"
          }  tracking-[0] font-medium m-0 pb-[15px] pr-[var(--spacing-sixteen)] min-h-[1px]`}
          data-testid="title"
        />
      )}
      {(isPageEditing || fields.SubTitle?.value) && (
        <RichText
          tag={typeOfSubtitleTag}
          field={fields.SubTitle}
          className={`richtext-content ${textColor} ${getConfiguredHeadingStyles(
            typeOfSubtitleTag,
          )}  font-normal tracking-[0.4px]  lg:tracking-[0.8px] font-medium mb-[10px] mt-[20px] pr-[var(--spacing-sixteen)] min-h-[1px]`}
          data-testid="sub-title"
        />
      )}
      {(isPageEditing || fields.Description?.value) && (
        <div
          className={`description-text mb-[20px] text-base tracking-[0.4px] leading-[24px] lg:tracking-[0.8px] font-normal mb-[20px] mt-[0px] pr-[var(--spacing-sixteen)] pt-[20px] min-h-[1px] ${textColor}`}
        >
          <RichTextWithTable
            field={fields.Description}
            data-testid="description-text"
            className="richtext-content"
          />
        </div>
      )}
      {(fields.PrimaryCTA?.value?.text ||
        fields.SecondaryCTA?.value?.text ||
        isPageEditing) && (
        <div className="md:flex md:flex-wrap gap-y-4 mb-[20px]">
          {(isPageEditing || fields.PrimaryCTA?.value?.text) && (
            <Button
              buttonType={primaryButtonType}
              className=""
              field={fields.PrimaryCTA}
              iconName={primaryIconName}
              tabIndex={0}
              aria-label={fields.PrimaryCTA?.value?.text}
              data-testid="primary-cta"
              data-role="link"
            />
          )}
          {(isPageEditing || fields.SecondaryCTA?.value?.text) && (
            <Button
              buttonType={secondaryButtonType}
              className=""
              field={fields.SecondaryCTA}
              iconName={secondaryIconName}
              tabIndex={0}
              aria-label={fields.SecondaryCTA?.value?.text}
              data-testid="secondary-cta"
              data-role="link"
            />
          )}
        </div>
      )}
    </div>
  );
};

export const SectionHeaderCentered = (
  props: SectionHeaderProps,
): JSX.Element => {
  console.log("---->[SectionHeaderCentered] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;

  if (!fields) return <SectionHeaderDefaultComponent {...props} />;

  const id = params?.RenderingIdentifier;
  const isDarkBg = computeIsDarkBg(params?.Styles);
  const textColor = isDarkBg ? "text-white" : "text-text-gray";
  const dividerStyle =
    params?.NoDivider === "1" ? "" : styles.sectionHeaderCenteredTitle;
  const typeOfTitleTag = params?.TypeOfTitleTag?.toLowerCase();
  const typeOfSubtitleTag = params?.TypeOfSubtitleTag?.toLowerCase() || "h3";
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);
  const hasAnyCTA =
    hasCtaLink(fields.PrimaryCTA) || hasCtaLink(fields.SecondaryCTA);
  const paddingClass = hasAnyCTA
    ? "pb-[var(--global-component-spacing)]"
    : "pb-0";
  const xmcStyles = props?.params?.Styles?.replace("text-8xl", "");
  const variantSuffix = isDarkBg ? "-reverse" : "";

  const primaryCtaClass = fields.PrimaryCTA?.value?.class;
  const secondaryCtaClass = fields.SecondaryCTA?.value?.class;

  const primaryButtonType = primaryCtaClass || `primary${variantSuffix}`;
  const secondaryButtonType = secondaryCtaClass || `secondary${variantSuffix}`;

  const primaryIconName = primaryCtaClass ? "CHEVRON_BLUE_RIGHT" : "";
  const secondaryIconName = secondaryCtaClass ? "CHEVRON_BLUE_RIGHT" : "";

  return (
    <div
      id={id ?? undefined}
      className={`section-header-blue-title-centered-component text-center px-[var(--spacing-sixteen)] ${paddingClass} pt-4 ${styles.sectionHeaderDefault} ${xmcStyles} ${hiddenBlockStyles}`}
      data-testid="section-header-blue-title-centered-component"
      data-role="section-header-blue-title-centered-component"
    >
      {(isPageEditing || fields.Title?.value) && (
        <RichText
          tag={typeOfTitleTag || "h2"}
          field={fields.Title}
          className={`${dividerStyle} richtext-content relative ${textColor} ${
            typeOfTitleTag
              ? getConfiguredHeadingStyles(typeOfTitleTag)
              : "text-[34px] leading-[42.16px] lg:text-[42px] lg:leading-[52.08px]"
          }  tracking-[0] font-medium m-0 pb-[15px] pr-[var(--spacing-sixteen)] min-h-[1px] mb-5`}
          data-testid="title"
        />
      )}
      {(isPageEditing || fields.SubTitle?.value) && (
        <RichText
          tag={typeOfSubtitleTag}
          field={fields.SubTitle}
          className={`richtext-content ${textColor} ${getConfiguredHeadingStyles(
            typeOfSubtitleTag,
          )} font-normal tracking-[0.4px] lg:tracking-[0.8px] font-medium mb-[10px] mt-[20px] pr-[var(--spacing-sixteen)] min-h-[1px]`}
          data-testid="sub-title"
        />
      )}
      {(isPageEditing || fields.Description?.value) && (
        <div
          className={`mb-[20px] text-base text-left tracking-[0.4px] leading-[24px] lg:tracking-[0.8px] font-normal mb-[20px] mt-[0px] pr-[var(--spacing-sixteen)] pt-[20px] min-h-[80px] ${textColor}`}
        >
          <RichTextWithTable
            field={fields.Description}
            data-testid="description-text"
            className="richtext-content"
          />
        </div>
      )}
      {(fields.PrimaryCTA?.value?.text ||
        fields.SecondaryCTA?.value?.text ||
        isPageEditing) && (
        <div className="flex items-center justify-center mb-[20px] flex-wrap">
          {(isPageEditing || fields.PrimaryCTA?.value?.text) && (
            <Button
              buttonType={primaryButtonType}
              className=""
              field={fields.PrimaryCTA}
              iconName={primaryIconName}
              tabIndex={0}
              aria-label={fields.PrimaryCTA?.value?.text}
              data-testid="primary-cta"
              data-role="link"
            />
          )}
          {(isPageEditing || fields.SecondaryCTA?.value?.text) && (
            <Button
              buttonType={secondaryButtonType}
              className=""
              field={fields.SecondaryCTA}
              iconName={secondaryIconName}
              tabIndex={0}
              aria-label={fields.SecondaryCTA?.value?.text}
              data-testid="primary-cta"
              data-role="link"
            />
          )}
        </div>
      )}
    </div>
  );
};

export const SectionHeaderSideBySide = (
  props: SectionHeaderProps,
): JSX.Element => {
  console.log("---->[SectionHeaderSideBySide] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;

  if (!fields) return <SectionHeaderDefaultComponent {...props} />;

  const id = params?.RenderingIdentifier;
  const stylesArray = params?.Styles?.split(" ") ?? [];
  const dividerStyleTextCta =
    params?.NoDivider === "1" ? "" : styles.sectionHeaderTextCta;
  const dividerStyleText =
    params?.NoDivider === "1" ? "" : styles.sectionHeaderText;
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);
  const hasAnyCTA =
    hasCtaLink(fields.PrimaryCTA) || hasCtaLink(fields.SecondaryCTA);
  const paddingClass = hasAnyCTA
    ? "pb-[var(--global-component-spacing)]"
    : "pb-0";

  const isDarkBg = computeIsDarkBg(params?.Styles);
  const withTextCta = stylesArray.includes("basic-promo-cta");
  const textColor = isDarkBg ? "text-white" : "text-gray";
  const ctaVariant = isDarkBg ? "primary-reverse" : "primary";
  const typeOfTitleTag = params?.TypeOfTitleTag?.toLowerCase();
  const xmcStyles = props?.params?.Styles?.replace("text-8xl", "");

  const wrapperClass = withTextCta
    ? "section-header-side-by-side-cta-component"
    : `section-header-side-by-side-component ${paddingClass}`;

  const containerClass = withTextCta
    ? "lg:pt-[64px] px-[var(--spacing-sixteen)] pb-[60px] lg:max-w-[438px] relative min-h-[1px]"
    : "w-full mx-auto flex flex-col lg:flex-row justify-center";

  const innerBlockClass = withTextCta
    ? "leading-[1.5] tracking-[0.4px] mb-[40px] lg:mb-[20px] m-0 font-normal"
    : "lg:max-w-[586px] lg:pl-[27px] lg:pr-0 lg:border-l-[2px] lg:border-teal lg:border-t-0 lg:leading-[30px] inline-block w-full pl-[44px] pr-[24px] m-0 leading-[28px] font-normal text-left text-gray tracking-[0]";

  const headingStyle = typeOfTitleTag
    ? getConfiguredHeadingStyles(typeOfTitleTag)
    : "";

  const textCtaHeadingStyle = headingStyle || "text-[32px] !leading-[40px]";

  const defaultHeadingStyle =
    headingStyle ||
    "!text-[32px] leading-[38px] lg:!text-[42px] lg:leading-[52px]";

  const titleClass = withTextCta
    ? `${dividerStyleTextCta} ${textColor} ${textCtaHeadingStyle} tracking-[0] font-medium m-0`
    : `${dividerStyleText} ${defaultHeadingStyle} w-full text-ecolab-blue text-center not-italic font-medium z-10 tracking-[0.4px] m-0`;

  const titleWrapperClass = withTextCta
    ? "relative mb-[20px] pb-[20px] m-0 p-0"
    : "lg:flex lg:justify-center lg:items-center lg:mr-[80px] lg:p-0 pb-[34px]";

  const descriptionClass = withTextCta
    ? "[&_*]:text-base [&_p]:!leading-[24px]"
    : "[&_*]:!text-xl [&_p]:lg:!leading-[30px] [&_p]:!leading-[28px]";

  const primaryCtaClass = fields.PrimaryCTA?.value?.class;

  const buttonType = primaryCtaClass || ctaVariant;

  const iconName = primaryCtaClass ? "CHEVRON_BLUE_RIGHT" : "";

  const shouldRenderTitle = Boolean(fields.Title?.value || isPageEditing);

  const shouldRenderDescription = Boolean(
    fields.Description?.value || isPageEditing,
  );

  return (
    <div
      className={`${styles.sectionHeader} ${wrapperClass} ${xmcStyles} ${hiddenBlockStyles}`}
      id={id ?? undefined}
      data-testid={withTextCta ? "banner-component" : "content-block-component"}
      data-role="section-header-side-by-side-component"
    >
      <div className={containerClass}>
        {shouldRenderTitle && (
          <div className={titleWrapperClass}>
            <RichText
              tag={typeOfTitleTag || "h2"}
              className={`richtext-content ${titleClass}`}
              field={fields.Title}
              data-testid="title"
            />
          </div>
        )}

        <div className={innerBlockClass}>
          {shouldRenderDescription && (
            <div
              className={`mb-[10px] [&_p]:!tracking-[0.4px] [&_p]:!lg:tracking-[0.8px] [&_p]:!font-normal mt-[0px] min-h-[80px] ${descriptionClass} ${textColor}`}
            >
              <RichTextWithTable
                field={fields.Description}
                data-testid="description-text"
                className="richtext-content"
              />
            </div>
          )}
        </div>

        {withTextCta && (
          <Button
            buttonType={buttonType}
            className="justify-self-start z-[1]"
            field={fields.PrimaryCTA}
            iconName={iconName}
            tabIndex={0}
            aria-label={fields.PrimaryCTA?.value?.text}
            data-testid="primary-cta"
            data-role="link"
          />
        )}
      </div>
    </div>
  );
};

export const SectionHeaderBlueTitle = (
  props: SectionHeaderProps,
): JSX.Element => {
  console.log("---->[SectionHeaderBlueTitle] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;

  if (!fields) return <SectionHeaderDefaultComponent {...props} />;

  const id = params?.RenderingIdentifier;
  const isDarkBg = computeIsDarkBg(params?.Styles);
  const textColor = isDarkBg ? "text-white" : "text-text-gray";
  const dividerStyle =
    params?.NoDivider === "1" ? "" : styles.sectionHeaderHeader;
  const typeOfTitleTag = params?.TypeOfTitleTag?.toLowerCase();
  const typeOfSubtitleTag = params?.TypeOfSubtitleTag?.toLowerCase() || "h3";
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);
  const hasAnyCTA =
    hasCtaLink(fields.PrimaryCTA) || hasCtaLink(fields.SecondaryCTA);
  const paddingClass = hasAnyCTA
    ? "pb-[var(--global-component-spacing)]"
    : "pb-0";
  const xmcStyles = props?.params?.Styles?.replace("text-8xl", "");

  const titleColorClass = isDarkBg ? "text-white" : "text-ecolab-blue";

  const titleHeadingStyle = typeOfTitleTag
    ? getConfiguredHeadingStyles(typeOfTitleTag)
    : "text-[34px] leading-[42.16px] lg:text-[42px] lg:leading-[52.08px]";

  const primaryCtaClass = fields.PrimaryCTA?.value?.class;
  const secondaryCtaClass = fields.SecondaryCTA?.value?.class;
  const variantSuffix = isDarkBg ? "-reverse" : "";

  const primaryButtonType = primaryCtaClass || `primary${variantSuffix}`;
  const secondaryButtonType = secondaryCtaClass || `secondary${variantSuffix}`;

  const primaryIconName = primaryCtaClass ? "CHEVRON_BLUE_RIGHT" : "";
  const secondaryIconName = secondaryCtaClass ? "CHEVRON_BLUE_RIGHT" : "";

  return (
    <div
      id={id ?? undefined}
      className={`section-header-blue-title-component px-[var(--spacing-sixteen)] ${paddingClass} pt-4 ${styles.sectionHeaderBlueTitle} ${xmcStyles} ${hiddenBlockStyles}`}
      data-testid="section-header-blue-title-component"
      data-role="section-header-blue-title-component"
    >
      {(isPageEditing || fields.Title?.value) && (
        <RichText
          tag={typeOfTitleTag || "h2"}
          field={fields.Title}
          className={`${dividerStyle} richtext-content relative ${titleColorClass} ${titleHeadingStyle} tracking-[0] font-medium m-0 pb-[15px] pr-[var(--spacing-sixteen)] min-h-[1px]`}
          data-testid="title"
        />
      )}
      {(isPageEditing || fields.SubTitle?.value) && (
        <RichText
          tag={typeOfSubtitleTag}
          field={fields.SubTitle}
          className={`richtext-content ${textColor} ${getConfiguredHeadingStyles(
            typeOfSubtitleTag,
          )}  font-normal tracking-[0.4px] lg:tracking-[0.8px] font-medium mb-[10px] mt-[20px] pr-[var(--spacing-sixteen)] min-h-[1px]`}
          data-testid="sub-title"
        />
      )}
      {(isPageEditing || fields.Description?.value) && (
        <div
          className={`mb-[20px] text-base tracking-[0.4px] leading-[24px] lg:tracking-[0.8px] font-normal mt-[0px] pr-[var(--spacing-sixteen)] pt-[20px] min-h-[80px] ${textColor}`}
        >
          <RichTextWithTable
            field={fields.Description}
            data-testid="description-text"
            className="richtext-content"
          />
        </div>
      )}
      {(fields.PrimaryCTA?.value?.text ||
        fields.SecondaryCTA?.value?.text ||
        isPageEditing) && (
        <div className="mb-[20px] pb-4 flex justify-start flex-wrap">
          {(isPageEditing || fields.PrimaryCTA?.value?.text) && (
            <Button
              buttonType={primaryButtonType}
              className=""
              field={fields.PrimaryCTA}
              iconName={primaryIconName}
              tabIndex={0}
              aria-label={fields.PrimaryCTA?.value?.text}
              data-testid="primary-cta"
              data-role="link"
            />
          )}
          {(isPageEditing || fields.SecondaryCTA?.value?.text) && (
            <Button
              buttonType={secondaryButtonType}
              className=""
              field={fields.SecondaryCTA}
              iconName={secondaryIconName}
              tabIndex={0}
              aria-label={fields.SecondaryCTA?.value?.text}
              data-testid="secondary-cta"
              data-role="link"
            />
          )}
        </div>
      )}
    </div>
  );
};

export const SectionHeaderBlueTitleCentered = (
  props: SectionHeaderProps,
): JSX.Element => {
  console.log("---->[SectionHeaderBlueTitleCentered] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;

  if (!fields) return <SectionHeaderDefaultComponent {...props} />;

  const id = params?.RenderingIdentifier;
  const isDarkBg = computeIsDarkBg(params?.Styles);
  const textColor = isDarkBg ? "text-white" : "text-text-gray";
  const titleColor = isDarkBg ? "text-white" : "text-ecolab-blue";
  const variantSuffix = isDarkBg ? "-reverse" : "";

  const dividerStyle =
    params?.NoDivider === "1" ? "" : styles.sectionHeaderHeaderCentered;
  const typeOfTitleTag = params?.TypeOfTitleTag?.toLowerCase();
  const typeOfSubtitleTag = params?.TypeOfSubtitleTag?.toLowerCase() || "h3";
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);
  const hasAnyCTA =
    hasCtaLink(fields.PrimaryCTA) || hasCtaLink(fields.SecondaryCTA);
  const paddingClass = hasAnyCTA
    ? "pb-[var(--global-component-spacing)]"
    : "pb-0";
  const xmcStyles = props?.params?.Styles?.replace("text-8xl", "");

  const titleHeadingClass = typeOfTitleTag
    ? getConfiguredHeadingStyles(typeOfTitleTag)
    : "text-[34px] leading-[42.16px] lg:text-[42px] lg:leading-[52.08px]";

  const primaryCtaClass = fields.PrimaryCTA?.value?.class;
  const secondaryCtaClass = fields.SecondaryCTA?.value?.class;

  const primaryButtonType = primaryCtaClass || `primary${variantSuffix}`;
  const secondaryButtonType = secondaryCtaClass || `secondary${variantSuffix}`;

  const primaryIconName = primaryCtaClass ? "CHEVRON_BLUE_RIGHT" : "";
  const secondaryIconName = secondaryCtaClass ? "CHEVRON_BLUE_RIGHT" : "";

  const shouldRenderTitle = Boolean(fields.Title?.value || isPageEditing);
  const shouldRenderSubtitle = Boolean(fields.SubTitle?.value || isPageEditing);
  const shouldRenderDescription = Boolean(
    fields.Description?.value || isPageEditing,
  );

  const shouldRenderCtas = Boolean(
    fields.PrimaryCTA?.value?.text ||
    fields.SecondaryCTA?.value?.text ||
    isPageEditing,
  );

  return (
    <div
      id={id ?? undefined}
      className={`section-header-blue-title-centered-component text-center px-[var(--spacing-sixteen)] ${paddingClass} pt-4 ${styles.sectionHeaderBlueTitle} ${xmcStyles} ${hiddenBlockStyles}`}
      data-testid="section-header-blue-title-centered-component"
      data-role="section-header-blue-title-centered-component"
    >
      {shouldRenderTitle && (
        <RichText
          tag={typeOfTitleTag || "h2"}
          field={fields.Title}
          className={`${dividerStyle} richtext-content relative ${titleColor} ${titleHeadingClass} tracking-[0] font-medium m-0 pb-[15px] pr-[var(--spacing-sixteen)] min-h-[1px]`}
          data-testid="title"
        />
      )}
      {shouldRenderSubtitle && (
        <RichText
          tag={typeOfSubtitleTag}
          field={fields.SubTitle}
          className={`richtext-content ${textColor} ${getConfiguredHeadingStyles(
            typeOfSubtitleTag,
          )} font-normal tracking-[0.4px] lg:tracking-[0.8px] font-medium mb-[10px] mt-[20px] pr-[var(--spacing-sixteen)] min-h-[1px]`}
          data-testid="sub-title"
        />
      )}
      {shouldRenderDescription && (
        <div
          className={`mb-[20px] text-base text-left tracking-[0.4px] leading-[24px] lg:tracking-[0.8px] font-normal mt-[0px] pr-[var(--spacing-sixteen)] pt-[20px] min-h-[80px] ${textColor}`}
        >
          <RichTextWithTable
            field={fields.Description}
            data-testid="description-text"
            className="richtext-content"
          />
        </div>
      )}
      {shouldRenderCtas && (
        <div className="mb-[20px] pb-4 flex flex-wrap justify-center">
          {(isPageEditing || fields.PrimaryCTA?.value?.text) && (
            <Button
              buttonType={primaryButtonType}
              className=""
              field={fields.PrimaryCTA}
              iconName={primaryIconName}
              tabIndex={0}
              aria-label={fields.PrimaryCTA?.value?.text}
              data-testid="primary-cta"
              data-role="link"
            />
          )}
          {(isPageEditing || fields.SecondaryCTA?.value?.text) && (
            <Button
              buttonType={secondaryButtonType}
              className=""
              field={fields.SecondaryCTA}
              iconName={secondaryIconName}
              tabIndex={0}
              aria-label={fields.SecondaryCTA?.value?.text}
              data-testid="secondary-cta"
              data-role="link"
            />
          )}
        </div>
      )}
    </div>
  );
};

export const SectionHeaderCenteredTextOnly = (
  props: SectionHeaderProps,
): JSX.Element => {
  console.log("---->[SectionHeaderCenteredTextOnly] Component", props.fields);

  const { page, fields, params } = props;
  const isPageEditing = page.mode.isEditing;

  if (!fields) return <SectionHeaderDefaultComponent {...props} />;

  const id = params?.RenderingIdentifier || undefined;
  const stylesArray = params?.Styles?.split(" ") ?? [];
  const bgStyle = stylesArray.find((item) => item.startsWith("bg-"));
  const hiddenBlockStyles = extractHiddenBlock(params?.GridParameters);

  const isDarkBg = computeIsDarkBg(params?.Styles);
  const typeOfTitleTag = params?.TypeOfTitleTag?.toLowerCase();
  const titleTextColor = isDarkBg ? "text-white" : "text-ecolab-blue";
  const descTextColor = isDarkBg ? "text-white" : "text-gray";
  const primaryCtaVariant = isDarkBg ? "primary-reverse" : "primary";
  const secondaryCtaVariant = isDarkBg ? "secondary-reverse" : "secondary";
  const has8xl = stylesArray.includes("text-8xl");

  const defaultTitleHeadingStyle = has8xl
    ? "leading-[76.8px]"
    : "text-[42px] leading-[33.6px]";

  const titleHeadingStyle = typeOfTitleTag
    ? getConfiguredHeadingStyles(typeOfTitleTag)
    : defaultTitleHeadingStyle;

  const titleStyles = `${titleTextColor} ${titleHeadingStyle}`;

  const primaryCtaClass = fields.PrimaryCTA?.value?.class;
  const secondaryCtaClass = fields.SecondaryCTA?.value?.class;

  const primaryButtonType = primaryCtaClass || primaryCtaVariant;
  const secondaryButtonType = secondaryCtaClass || secondaryCtaVariant;

  const primaryIconName = primaryCtaClass ? "CHEVRON_BLUE_RIGHT" : "";
  const secondaryIconName = secondaryCtaClass ? "CHEVRON_BLUE_RIGHT" : "";

  const shouldRenderCtas = Boolean(
    fields.PrimaryCTA?.value?.text ||
    fields.SecondaryCTA?.value?.text ||
    isPageEditing,
  );

  return (
    <div
      id={id}
      className={`section-header-centered-text-only-component text-center px-[var(--spacing-sixteen)] py-[var(--global-component-spacing)] ${styles.sectionHeaderCenteredTextOnly} ${bgStyle ?? ""} ${params?.Styles ?? ""} ${hiddenBlockStyles}`}
      data-testid="section-header-centered-text-only-component"
      data-role="section-header-centered-text-only-component"
    >
      {(isPageEditing || fields.Title?.value) && (
        <RichText
          tag={typeOfTitleTag || "h2"}
          field={fields.Title}
          className={`${styles.sectionHeaderTitle} richtext-content relative tracking-[0.4px] lg:tracking-[0.8px] font-medium m-0 mb-[15px] min-h-[1px] ${titleStyles}`}
          data-testid="title"
        />
      )}
      {(isPageEditing || fields.Description?.value) && (
        <div
          className={`mb-[30px] text-xl tracking-[0.4px] leading-[30px] lg:tracking-[0.8px] font-normal mt-[0px] min-h-[80px] [&_*]:text-center! [&_*]:text-xl ${descTextColor}`}
        >
          <RichTextWithTable
            field={fields.Description}
            data-testid="description-text"
            className={`richtext-content ${styles.textOnlyCenteredList}`}
          />
        </div>
      )}
      {shouldRenderCtas && (
        <div className="md:flex gap-x-[10px] md:items-center md:justify-center mb-[20px] text-base">
          {(isPageEditing || fields.PrimaryCTA?.value?.text) && (
            <div className="mb-4 gap-x-[10px]">
              <Button
                buttonType={primaryButtonType}
                className="inline-flex justify-center items-center m-0!"
                field={fields.PrimaryCTA}
                iconName={primaryIconName}
                tabIndex={0}
                aria-label={fields.PrimaryCTA?.value?.text}
                data-testid="primary-cta"
                data-role="link"
              />
            </div>
          )}
          {(isPageEditing || fields.SecondaryCTA?.value?.text) && (
            <div className="mb-4 gap-x-[10px]">
              <Button
                buttonType={secondaryButtonType}
                className="inline-flex justify-center items-center m-0!"
                field={fields.SecondaryCTA}
                iconName={secondaryIconName}
                tabIndex={0}
                aria-label={fields.SecondaryCTA?.value?.text}
                data-testid="secondary-cta"
                data-role="link"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
